import Input from "../Input/Input"
import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { showSnackBar } from "../Snackbar/Snackbar"
import Button from "../Button/Button"
import { NavLink, useNavigate } from "react-router-dom"
import { areFieldsNotEmpty, isEmptyFields } from "../../helpers/helper"
import Calendar from "../Calendar/Calendar"
import { now, dateToServer, serverToLocal, serverToLocalDateOnly } from "../../utils/date"
import { phoneNumber, generateSSNumber, ssnForPrefill } from "../../utils/payment"
import { useMyContextLoading } from "../LoadingContextProvider/LoadingContextProvider"
import {
  changeUserCreator,
  editContactById,
  getAssignToInfo,
  getContactById
} from "../../features/customer/customerSlice"
import Dropdown from "../Dropdown/Dropdown"
import { CrossCloseIcon } from "../Icons/Icons"
import { formatUserWithContactGroups } from "../../utils/assignees"
import { StorageGetItem, USER } from "../../lib/storage"
import { fetchStates } from "../../features/states/states"
import { fetchTimeZones } from "../../features/timeZone/timeZone"
import { fetchAllAffiliatesDropdown } from "../../features/affiliates/affiliates"
import { getLeadDetails } from "../../features/contacts/contacts"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { CONTACT_EMAIL_OPTIONAL } from "../../config/featureFlagKeys"
import { emailOptionalFor } from "../../containers/Contacts/add-contact/emailOptional"

const UserGeneralTab = ({ assigneesAllowed, viewOnly = false, successNavTo = false, cancelNavTo = false }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { triggerLoading } = useMyContextLoading()
  const [reqError, setReqError] = useState('')
  const [assignees, setAssignees] = useState([])
  const [states, setStates] = useState([])
  const [timeZones, setTimeZone] = useState([])
  const [formData, setFormData] = useState({
    creator: '',
    firstName: '',
    lastName: '',
    secondaryEmail: '',
    primaryPhone: '',
    address: {
      address: '',
      aptNumber: '',
      city: '',
      state: '',
      zip: '',
    },
    secondaryPhone: '',
    gender: '',
    timeZone: '',
    ssNumber: '',
    middleName: '',
    enrollmentDate: now(),
    dateOfBirth: '',
  })
  const currentURL = window.location.href
  const parts = currentURL.split('/')
  const userId = parts[parts?.length - 1]
  const [assignedToDropdown, setAssignedToDropdown] = useState([])
  const [creatorsDropdown, setCreatorsDropdown] = useState([])
  const [newCreator, setNewCreator] = useState('')
  const [userRole, setUserRole] = useState('')
  // WS #2219705 (QA 10-19): a PACK client may have no email, so this page must save without one. Whether
  // the client's office is PACK comes from the lead GET, as on Add Contact; it is asked only with the flag on.
  const contactEmailOptional = useSelector(selectFeatureFlag(CONTACT_EMAIL_OPTIONAL))
  const [contactGroupConfig, setContactGroupConfig] = useState(null)
  const emailOptional = emailOptionalFor({ flagOn: contactEmailOptional, contactGroupConfig })
  useEffect(() => {
    StorageGetItem(USER).then(result => {
      setUserRole(result?.role)
    })
  }, [])

  const getAllAffiliatesDropdownInfo = async () => {
    triggerLoading(true)
    try {
      const user = await StorageGetItem(USER)
      if (user?.role === 'customer' || user?.role === 'user') {
        const response = await dispatch(getAssignToInfo())

        if (response?.payload?.data) {
          setAssignedToDropdown(response?.payload?.data?.length > 0 ? response?.payload?.data?.map(user => ({
            label: formatUserWithContactGroups({ user, userRole }),
            value: user?._id
          })) : setAssignedToDropdown)
        }
      }
    } catch (error) {
      setReqError(error.message || 'Failed to Fetch affiliates list')
    }
    triggerLoading(false)
  }

  const getAffiliatesDropdown = async () => {
    triggerLoading(true)
    try {
      const response = await dispatch(fetchAllAffiliatesDropdown({ affiliatesOnly: true }))
      const affiliates = response?.payload?.data

      if (affiliates && affiliates.length > 0) {
        setCreatorsDropdown(affiliates)
      }
    } catch (error) {
      setReqError(error.message || 'Failed to Fetch affiliates list')
    }
    triggerLoading(false)
  }

  useEffect(() => {
    const handleReqError = () => {
      reqError?.length > 0 && showSnackBar("error", reqError)
      setReqError('')
    }

    handleReqError()
  }, [reqError])

  const handleEditUser = async () => {
    triggerLoading(true)
    const newNumber = formData?.primaryPhone?.replace(/\D/g, "")
    if (newCreator) {
      await dispatch(changeUserCreator({
        userId,
        newCreatorId: newCreator
      }))
    }

    if (userRole !== 'affiliate') {
      delete formData.creator
      const response = await dispatch(editContactById({
        userId,
        userData: {
          ...formData,
          primaryPhone: newNumber,
          assignees,
          dateOfBirth: dateToServer(formData.dateOfBirth),
          enrollmentDate: dateToServer(formData.enrollmentDate),
        }
      }))

      if (response.error) {
        setReqError(response.payload)
      } else if (response) {
        navigate(successNavTo || "/")
        showSnackBar("success", "Your changes are saved")
        setFormData({
          creator: '',
          firstName: '',
          lastName: '',
          secondaryEmail: '',
          primaryPhone: '',
          address: {
            address: '',
            aptNumber: '',
            city: '',
            state: '',
            zip: '',
          },
          ssNumber: '',
          dateOfBirth: ''
        }
        )
      }
    }

    triggerLoading(false)
  }

  const getUserById = async () => {
    triggerLoading(true)
    try {
      const response = await dispatch(getContactById(userId))
      const {
        creator, firstName, lastName, secondaryEmail, primaryPhone, address, ssNumber, dateOfBirth, assignees,
        middleName, gender, timeZone, enrollmentDate, secondaryPhone
      } = response?.payload?.data
      setFormData({
        creator: creator || '',
        firstName: firstName || '',
        lastName: lastName || '',
        secondaryEmail: secondaryEmail || '',
        primaryPhone: phoneNumber(primaryPhone) || '',
        secondaryPhone: phoneNumber(secondaryPhone) || '',
        address: {
          address: address?.address || '',
          aptNumber: address?.aptNumber || '',
          city: address?.city || '',
          state: address?.state || '',
          zip: address?.zip || '',
        },
        ssNumber: ssnForPrefill(ssNumber),
        dateOfBirth: serverToLocalDateOnly(dateOfBirth) || '',
        timeZone: timeZone || '',
        gender: gender || '',
        enrollmentDate: serverToLocalDateOnly(enrollmentDate) || '',
        middleName: middleName || '',
      })
      setAssignees(assignees || [])
    } catch (error) {
      console.error(error)
      setReqError(error.message || 'Failed to get customer data')
    }
    triggerLoading(false)
  }

  const getStates = async () => {
    try {
      const response = await dispatch(fetchStates({ searchQuery: '' }))
      setStates(response?.payload?.data)
    } catch (error) {
      showSnackBar("error", error)
    }
  }

  const getTimeZones = async () => {
    try {
      const response = await dispatch(fetchTimeZones({ searchQuery: '' }))
      setTimeZone(response?.payload?.data)
    } catch (error) {
      showSnackBar("error", error)
    }
  }

  useEffect(() => {
    if (contactEmailOptional !== true || !userId) return
    dispatch(getLeadDetails(userId))
      .then(r => setContactGroupConfig(r?.payload?.data?.contactGroupConfig || null))
      .catch(() => {})
  }, [contactEmailOptional, userId])

  useEffect(() => {
    const fetch = async () => {
      const user = await StorageGetItem(USER)
      if (user?.role !== 'affiliate') {
        await getUserById()
        await getStates()
        await getTimeZones()
        await getAllAffiliatesDropdownInfo()
        await getAffiliatesDropdown()
      }
    }
    fetch()
  }, [])


  const handleInputChange = ({ name, value }) => {
    switch (name) {
      case 'ssNumber':
        setFormData((prevData) => ({ ...prevData, [name]: generateSSNumber(value) }))
        break
      case 'primaryPhone':
        setFormData((prevData) => ({ ...prevData, [name]: phoneNumber(value) }))
        break
      default:
        const [parentField, childField] = name.split('.')
        if (childField) {
          setFormData((prevData) => ({
            ...prevData,
            [parentField]: {
              ...prevData[parentField],
              [childField]: value
            }
          }))
        } else {
          setFormData((prevData) => ({
            ...prevData,
            [name]: value
          }))
        }
        break
    }
  }

  const getAssigneesData = ({ assignee }) => {
    const user = assignedToDropdown?.find(el => el.value === assignee)
    if (user) {
      return user.label
    }
  }

  const disableSubmitButton = !areFieldsNotEmpty([formData.firstName, formData.lastName, ...(emailOptional ? [] : [formData.secondaryEmail]), formData.ssNumber]) || formData.dateOfBirth === ''

  return (
    <div className="flex  w-full items-center justify-center">
      {/*<AddAvatar/>*/}
      <div className="w-full max-w-[850px] bg-surface border border-divider rounded-2xl shadow-card p-10">
        <div className="grid grid-cols-2 gap-x-4 gap-y-6 pb-6 h-max border-b border-divider ">
          {(userRole === 'customer' || userRole === 'user') &&
            <Input width="w-full" placeholder="Enter First Name" label="First name *" value={formData.firstName}
              onChange={(e) => handleInputChange({ name: 'firstName', value: e.target.value })}
              disabled={viewOnly} />}
          {(userRole === 'customer' || userRole === 'user') &&
            <Input width="w-full" placeholder="Enter Last Name" label="Last name *" value={formData.lastName}
              onChange={(e) => handleInputChange({ name: 'lastName', value: e.target.value })}
              disabled={viewOnly} />}
          {(userRole === 'customer' || userRole === 'user') &&
            <Input width="w-full" placeholder="Enter Middle Name" label="Middle name" value={formData.middleName}
              onChange={(e) => handleInputChange({ name: 'middleName', value: e.target.value })}
              disabled={viewOnly} />}
          {(userRole === 'customer' || userRole === 'user') &&
            <Input width="w-full" placeholder="Enter Primary Phone" label="Primary Phone" value={formData.primaryPhone}
              onChange={(e) => handleInputChange({ name: 'primaryPhone', value: e.target.value })}
              disabled={viewOnly} />}
          {(userRole === 'customer' || userRole === 'user') &&
            <Input width="w-full" placeholder="Enter Secondary Phone" label="Secondary Phone"
              value={formData.secondaryPhone}
              onChange={(e) => handleInputChange({ name: 'secondaryPhone', value: e.target.value })}
              disabled={viewOnly} />}
          {(userRole === 'customer' || userRole === 'user') &&
            <Input type="email" width="w-full" placeholder="Enter portal login email" label={emailOptional ? "Portal login email (optional)" : "Portal login email *"}
              value={formData.secondaryEmail}
              onChange={(e) => handleInputChange({ name: 'secondaryEmail', value: e.target.value })}
              disabled={viewOnly} />}
          {(userRole === 'customer' || userRole === 'user') &&
            <Input width="w-full" placeholder="Enter Address" label="Address" value={formData.address.address}
              onChange={(e) => handleInputChange({ name: 'address.address', value: e.target.value })}
              disabled={viewOnly} />}
          {(userRole === 'customer' || userRole === 'user') &&
            <Input width="w-full" placeholder="34" label="Suite/Apt#" value={formData.address.aptNumber}
              onChange={(e) => handleInputChange({ name: 'address.aptNumber', value: e.target.value })}
              disabled={viewOnly} />}
          {(userRole === 'customer' || userRole === 'user') &&
            <Input width="w-full" placeholder="Enter City" label="City" value={formData.address.city}
              onChange={(e) => handleInputChange({ name: 'address.city', value: e.target.value })}
              disabled={viewOnly} />}
          {(userRole === 'customer' || userRole === 'user') && <Dropdown
            menuItems={states?.map((state) => ({
              label: `${state.shortName} - ${state.fullName}`,
              value: state.shortName,
            }))}
            label="State"
            placeholder="Enter State"
            value={formData.address.state}
            type="text"
            name="state"
            className="form-control w-full"
            required={true}
            setValue={(e) => handleInputChange({ name: 'address.state', value: e })}
          />}
          {(userRole === 'customer' || userRole === 'user') &&
            <Input width="w-full" placeholder="Enter Zip" label="Zip" value={formData.address?.zip}
              onChange={(e) => handleInputChange({ name: 'address.zip', value: e.target.value })}
              disabled={viewOnly} />}
          {(userRole === 'customer' || userRole === 'user') &&
            <Input width="w-full" placeholder="Enter Social Security Number" label="Social Security Number *"
              value={formData.ssNumber}
              onChange={(e) => handleInputChange({ name: 'ssNumber', value: e.target.value })}
              disabled={viewOnly} />}
          {(userRole === 'customer' || userRole === 'user') && <Calendar
            width="w-full"
            height="56px"
            label="Date Of Birth *"
            optional
            value={formData.dateOfBirth}
            onChange={(e) => handleInputChange({ name: 'dateOfBirth', value: e })}
            errorMsg={isEmptyFields(formData?.dateOfBirth) ? 'This field is required' : ''}
            isRequired={true}
            disabled={viewOnly}
          />}
          {(userRole === 'customer' || userRole === 'user') && <Calendar
            width="w-full"
            height="56px"
            label="Enrollment Date"
            value={formData.enrollmentDate}
            onChange={(e) => handleInputChange({ name: 'enrollmentDate', value: e })}
            isRequired={true}
            disabled={viewOnly}
          />}
          {(userRole === 'customer' || userRole === 'user') && <Dropdown
            menuItems={[
              { label: 'Male', value: 'male', },
              { label: 'Female', value: 'female', }
            ]}
            label="Gender"
            placeholder="Select Gender"
            value={formData.gender}
            type="text"
            name="gender"
            className="form-control w-full"
            required={true}
            setValue={(e) => handleInputChange({ name: 'gender', value: e })}
          />}
          {(userRole === 'customer' || userRole === 'user') && <Dropdown
            menuItems={timeZones?.map((timeZone) => ({
              label: timeZone.shortName,
              value: timeZone.shortName,
            }))}
            label="Time Zone"
            placeholder="Enter Time Zone"
            value={formData.timeZone}
            type="text"
            name="timeZones"
            className="form-control w-full"
            required={true}
            setValue={(e) => handleInputChange({ name: 'timeZone', value: e })}
          />}
          {(userRole === 'customer' || userRole === 'user') && <div>
            {assigneesAllowed && <Dropdown
              value={assignees}
              setValue={(e) => setAssignees(prev => [...prev, e])}
              menuItems={assignedToDropdown?.filter(affiliate => !assignees.includes(affiliate?.value))}
              className="w-full mt-2"
              variant="standard"
              placeholder="John Doe"
              label="Assignees"
              disabled={viewOnly}
            />}
            <div className="w-[350px] flex flex-wrap text-md mt-4  basis-1/2 ">
              {assignees?.map((assignee, index) => (
                <div
                  key={index}
                  className="flex flex-row items-center justify-between whitespace-normal break-words mr-4 mb-2"
                >
                  <div className="text-wrap">{getAssigneesData({ assignee })}</div>
                  <button className="ml-2" onClick={() => setAssignees(prev => prev.filter(el => el !== assignee))}>
                    <CrossCloseIcon fill="#667085" />
                  </button>
                </div>
              ))}

            </div>
          </div>}
          {(userRole === 'customer' || userRole === 'user') && <div>
            {assigneesAllowed && <Dropdown
              value={newCreator}
              setValue={(e) => setNewCreator(e)}
              menuItems={creatorsDropdown?.filter(affiliate => !assignees.includes(affiliate?.value))}
              className="w-full mt-2"
              variant="standard"
              placeholder="John Doe"
              label="Creator"
              disabled={viewOnly}
            />}
          </div>}
        </div>
        <div className={`mt-6 flex justify-end space-x-4`}>
          <NavLink to={cancelNavTo || "#"} onClick={(e) => {
            e.preventDefault()
            navigate(-1)
          }}>
            <Button classes={"mr-4"} onClick={() => {
            }} type="outlined" size="lg" width="min-w-[156px]">Close</Button>
          </NavLink>
          {(userRole === 'customer' || userRole === 'user') &&
            <Button onClick={handleEditUser} size="lg" width="min-w-[156px]"
              disabled={disableSubmitButton}
              classes="rounded-lg">Save</Button>}
        </div>
      </div>
    </div>
  )
}

export default UserGeneralTab
